import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import ComicCard from "@/components/feature/ComicCard";
import { categories, comics } from "@/mocks/comics";

export default function Recommend() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "全部";
  const q = params.get("q") || "";
  const [keyword, setKeyword] = useState(q);

  const allCats = ["全部", ...categories.map((c) => c.name)];

  const list = useMemo(() => {
    let result = comics;
    if (cat !== "全部") result = result.filter((c) => c.genre === cat);
    if (q) {
      const kw = q.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.includes(kw)) ||
          c.desc.includes(kw) ||
          c.genre.includes(kw)
      );
    }
    return result;
  }, [cat, q]);

  const setCat = (c: string) => {
    const next = new URLSearchParams(params);
    if (c === "全部") next.delete("cat");
    else next.set("cat", c);
    setParams(next, { replace: true });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "无脑爽漫推荐",
    description: "炮渣漫画网收录的无脑爽漫、开挂主角漫画、逆袭流合集与打脸爽文漫推荐列表。",
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }} />

      <Navbar />
      <main>
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "无脑爽漫推荐" }]}
          title="无脑爽漫推荐"
          subtitle="开挂主角、逆袭流、龙傲天与打脸爽文漫精选合集。按分类或关键词筛选，点开就能直接开刷。"
        />

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          {/* 筛选栏 */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="漫画分类">
              {allCats.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                    cat === c ? "bg-primary-500 text-background-50 font-semibold" : "bg-background-100 text-foreground-600 border border-background-300/50 hover:text-primary-400"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <form onSubmit={handleSearch} className="flex items-center gap-2 max-w-md">
              <div className="flex items-center flex-1 gap-2 rounded-full bg-background-100 border border-background-300/60 px-4 py-2.5 focus-within:border-primary-500/60">
                <i className="ri-search-line text-foreground-600 text-sm"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索爽漫标题或标签…"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-700"
                  aria-label="搜索漫画"
                />
              </div>
              <button type="submit" className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors">
                搜索
              </button>
            </form>
          </div>

          {/* 结果提示 */}
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950">
              {q ? `“${q}” 的搜索结果` : cat === "全部" ? "全部推荐" : `${cat}合集`}
              <span className="ml-2 text-sm text-foreground-600">{list.length} 部作品</span>
            </h2>
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {list.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg bg-background-100 border border-background-300/50 p-14 text-center">
              <i className="ri-search-eye-line text-5xl text-foreground-700"></i>
              <h3 className="mt-4 font-heading text-lg text-foreground-950">没有找到相关作品</h3>
              <p className="mt-2 text-sm text-foreground-600">换个关键词试试，比如「炼丹」「都市」「雷霆」等爽点标签。</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}